import { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { API_BASE, API_HOST } from "config/api";

const AuthContext = createContext();

// Role definitions for easier management
const ROLES = {
  ADMIN: "admin",
  PASTOR: "pastor",
  STAFF: "staff",
  MEMBER: "member",
  BRANCH_ADMIN: "branch_admin",
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [church, setChurch] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(null);
  const navigate = useNavigate();

  // Fetch church data with error handling
  const fetchChurchData = async (churchId) => {
    try {
      const response = await fetch(`${API_BASE}/churches/list/${churchId}`);
      if (response.ok) {
        const json = await response.json();
        // Tolerate both shapes: a flat model OR a {status, data} envelope.
        const churchData = json?.id ? json : json?.data || json;
        setChurch(churchData);
        localStorage.setItem("church", JSON.stringify(churchData));
        return churchData;
      }
      throw new Error("Failed to fetch church data");
    } catch (error) {
      console.error("Error fetching church data:", error);
      return null;
    }
  };

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      const storedUser = localStorage.getItem("user");
      const storedToken = localStorage.getItem("authToken");

      if (storedUser && storedToken) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          setToken(storedToken);

          // Check if we have church data in localStorage
          const storedChurch = localStorage.getItem("church");
          if (storedChurch) {
            setChurch(JSON.parse(storedChurch));
          } else if (parsedUser.church_id) {
            await fetchChurchData(parsedUser.church_id);
          }

          // Re-sync the user from the server so role/permissions changes an
          // admin made while this session was active (e.g. granting module
          // access) take effect on next load instead of staying stuck at
          // whatever was cached in localStorage at login.
          try {
            const meRes = await fetch(`${API_BASE}/user`, {
              headers: { Accept: "application/json", Authorization: `Bearer ${storedToken}` },
            });
            if (meRes.ok) {
              const meJson = await meRes.json();
              const freshUser = meJson?.user || meJson?.data || meJson;
              if (freshUser?.id) {
                setUser(freshUser);
                localStorage.setItem("user", JSON.stringify(freshUser));
              }
            }
          } catch (refreshError) {
            console.error("Error refreshing user from server:", refreshError);
          }
        } catch (error) {
          console.error("Error parsing stored user data:", error);
          logout();
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  // Login function with role handling
  const login = async (userData, newToken) => {
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("authToken", newToken);
    setUser(userData);
    setToken(newToken);

    // console.log(userData.member_id);

    if (userData.church_id) {
      await fetchChurchData(userData.church_id);
    }

    // Redirect based on role
    redirectBasedOnRole(userData.role);
  };

  // Redirect users based on their role
  const redirectBasedOnRole = (role) => {
    switch (role) {
      case ROLES.ADMIN:
        navigate("/admin/dashboard");
        break;
      case ROLES.PASTOR:
        navigate("/pastor/dashboard");
        break;
      case ROLES.STAFF:
        navigate("/staff/dashboard");
        break;
      case ROLES.MEMBER:
        navigate("/member/dashboard");
        break;
      case ROLES.BRANCH_ADMIN:
        navigate("/dashboard");
        break;
      default:
        navigate("/dashboard");
    }
  };

  // Logout function
  const logout = async () => {
    // API call to invalidate the token on the server
    const myHeaders = new Headers();
    myHeaders.append("Accept", "application/json");
    // Use the current token from state, not a hardcoded one
    if (token) {
      myHeaders.append("Authorization", `Bearer ${token}`);
    }
    myHeaders.append("Content-Type", "application/json");

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      redirect: "follow",
    };

    try {
      await fetch(`${API_BASE}/logout`, requestOptions);
    } catch (error) {
      console.error("Error during API logout:", error);
    } finally {
      // Clear all auth-related data from local storage
      localStorage.removeItem("user");
      localStorage.removeItem("authToken");
      localStorage.removeItem("church");
      localStorage.removeItem("rememberMe");

      // Reset state
      setUser(null);
      setToken(null);
      setChurch(null);

      // Navigate to the sign-in page after logout
      navigate("/authentication/sign-in", { replace: true });
    }
  };

  // Update the signed-in user's name/email and their church's profile fields
  // (including sms_sender_id). Persists to the API, then refreshes local
  // state + localStorage from the server response so the UI never drifts.
  const updateProfile = async (userFields, churchFields) => {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    if (user?.id) {
      const userRes = await fetch(`${API_BASE}/users/${user.id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(userFields),
      });
      if (!userRes.ok) throw new Error("Failed to update user profile");
      const userJson = await userRes.json();
      const updatedUser = { ...user, ...(userJson?.data || userJson) };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
    }

    if (church?.id) {
      const churchRes = await fetch(`${API_BASE}/churches/${church.id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(churchFields),
      });
      if (!churchRes.ok) throw new Error("Failed to update church profile");
      const churchJson = await churchRes.json();
      const updatedChurch = { ...church, ...(churchJson?.data || churchJson) };
      setChurch(updatedChurch);
      localStorage.setItem("church", JSON.stringify(updatedChurch));
    }
  };

  // Upload/replace the church's logo image. Sent as multipart with a PUT
  // method override (Laravel only populates $_FILES on POST), mirroring the
  // member photo upload pattern used elsewhere in the app.
  const uploadChurchLogo = async (file) => {
    if (!church?.id) return;
    const formData = new FormData();
    formData.append("logo", file);
    formData.append("_method", "PUT");

    const res = await fetch(`${API_BASE}/churches/${church.id}`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to upload church logo");
    }
    const json = await res.json();
    const updatedChurch = { ...church, ...(json?.data || json) };
    setChurch(updatedChurch);
    localStorage.setItem("church", JSON.stringify(updatedChurch));
    return updatedChurch;
  };

  // Self-service password change — requires the current password.
  const changePassword = async (currentPassword, newPassword, confirmPassword) => {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    const res = await fetch(`${API_BASE}/auth/change-password`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: confirmPassword,
      }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      const message =
        json?.errors?.current_password?.[0] || json?.message || "Failed to change password";
      throw new Error(message);
    }
    return json;
  };

  // Check if user has specific role
  const hasRole = (requiredRole) => {
    if (!user) return false;
    return user.role === requiredRole;
  };

  // Check if user has any of the required roles
  const hasAnyRole = (requiredRoles) => {
    if (!user) return false;
    return requiredRoles.includes(user.role);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        church,
        isLoading,
        token,
        login,
        logout,
        fetchChurchData,
        updateProfile,
        uploadChurchLogo,
        changePassword,
        isAuthenticated: !!user,
        // Role check helpers
        isAdmin: hasRole(ROLES.ADMIN),
        isPastor: hasRole(ROLES.PASTOR),
        isStaff: hasRole(ROLES.STAFF),
        isMember: hasRole(ROLES.MEMBER),
        hasRole,
        hasAnyRole,
        // Role constants
        ROLES,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
